import { SIGNIFICANT_HEIGHT_MIN_PX } from '../constants';

export interface WaveScaleFrame {
  width: number;
  height: number;
  baseY: number;
  /** Significant wave height, metres. */
  significantHeightM: number;
  /** The boat's own scale, px per metre. */
  pxPerM: number;
  /** Wave draw cap, px. */
  clampPx: number;
}

const CHIP_HEIGHT = 28;
const CHIP_PAD_X = 6;

const oneDecimal = (v: number): string => v.toFixed(1);

const roundedRect = (
  ctx: CanvasRenderingContext2D,
  x: number,
  y: number,
  width: number,
  height: number,
  radius: number,
): void => {
  ctx.beginPath();
  ctx.moveTo(x + radius, y);
  ctx.arcTo(x + width, y, x + width, y + height, radius);
  ctx.arcTo(x + width, y + height, x, y + height, radius);
  ctx.arcTo(x, y + height, x, y, radius);
  ctx.arcTo(x, y, x + width, y, radius);
  ctx.closePath();
};

/**
 * The wave-height scale on the right edge: a true-scale bracket anchored to the
 * same significant height the waves are drawn at.
 *
 * Trough cap to crest cap is the full height of the largest wave to expect, so
 * the user can eyeball the live waves rising against it. Labelled in metres to
 * match the description text under the card.
 */
export class WaveScaleRenderer {
  /** Text metrics are only re-measured when the label changes. */
  private cachedValue = '';

  private prefixWidth = 0;

  private valueWidth = 0;

  constructor(private readonly ctx: CanvasRenderingContext2D) {}

  draw({
    width,
    height,
    baseY,
    significantHeightM,
    pxPerM,
    clampPx,
  }: WaveScaleFrame): void {
    const { ctx } = this;

    // Significant height in px, exactly as the sea is drawn.
    const significantPx = Math.max(
      SIGNIFICANT_HEIGHT_MIN_PX,
      Math.min(clampPx * 0.98, significantHeightM * pxPerM),
    );

    const x = width - 22;
    const crestY = Math.max(10, baseY - significantPx);
    const troughY = Math.min(height - 4, baseY + significantPx);

    ctx.save();
    ctx.lineCap = 'round';
    ctx.lineJoin = 'round';

    const bracket = (): void => {
      ctx.beginPath();
      ctx.moveTo(x, troughY);
      ctx.lineTo(x, crestY); // spine: trough up to crest
      ctx.moveTo(x - 6, crestY);
      ctx.lineTo(x + 6, crestY); // crest cap
      ctx.moveTo(x - 5, troughY);
      ctx.lineTo(x + 5, troughY); // trough cap
    };

    // A dark outline carries the contrast against a bright sea, with the bright
    // bracket drawn on top of it.
    bracket();
    ctx.strokeStyle = 'rgba(0,0,0,0.5)';
    ctx.lineWidth = 4.5;
    ctx.stroke();
    bracket();
    ctx.strokeStyle = 'rgba(255,255,255,0.97)';
    ctx.lineWidth = 2.5;
    ctx.stroke();

    // "Max" stacked over the height, in a narrow chip seated on the scale
    const prefix = 'Max';
    const value = `${oneDecimal(2 * significantHeightM)}m`;

    if (value !== this.cachedValue) {
      ctx.font = '600 9px ui-sans-serif,system-ui,sans-serif';
      this.prefixWidth = ctx.measureText(prefix).width;
      ctx.font = '700 12px ui-sans-serif,system-ui,sans-serif';
      this.valueWidth = ctx.measureText(value).width;
      this.cachedValue = value;
    }

    const chipWidth =
      Math.max(this.prefixWidth, this.valueWidth) + CHIP_PAD_X * 2;
    const chipX = Math.max(
      4,
      Math.min(width - 4 - chipWidth, x - chipWidth / 2),
    );
    const chipCentreX = chipX + chipWidth / 2;
    const chipBottom = Math.max(CHIP_HEIGHT + 3, crestY - 4);
    const chipTop = chipBottom - CHIP_HEIGHT;

    roundedRect(ctx, chipX, chipTop, chipWidth, CHIP_HEIGHT, 5);
    ctx.fillStyle = 'rgba(8,20,32,0.8)';
    ctx.fill();

    ctx.textAlign = 'center';
    ctx.textBaseline = 'alphabetic';
    ctx.font = '600 9px ui-sans-serif,system-ui,sans-serif';
    ctx.fillStyle = 'rgba(255,255,255,0.8)';
    ctx.fillText(prefix, chipCentreX, chipTop + 12);
    ctx.font = '700 12px ui-sans-serif,system-ui,sans-serif';
    ctx.fillStyle = '#fff';
    ctx.fillText(value, chipCentreX, chipTop + 24);

    ctx.restore();
  }
}
